import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api, { extractError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  UserCircle, Calendar, CheckCircle2, Link2, Unlink, Loader2, AlertCircle,
  ShieldCheck, KeyRound, Copy, Download, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const Profile = () => {
  const { user } = useAuth();
  const [cal, setCal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();

  // Codici di riserva (2FA)
  const [bk, setBk] = useState(null);
  const [bkBusy, setBkBusy] = useState(false);
  const [bkCodes, setBkCodes] = useState([]);
  const [bkOpen, setBkOpen] = useState(false);

  const loadCal = async () => {
    try { const { data } = await api.get("/oauth/calendar/status"); setCal(data); }
    catch { setCal(null); }
  };

  const loadBk = async () => {
    try { const { data } = await api.get("/auth/2fa/backup-codes"); setBk(data); }
    catch { setBk(null); }
  };

  useEffect(() => { loadCal(); loadBk(); }, []);

  const generateBackupCodes = async () => {
    if (bk?.remaining > 0 && !window.confirm("Rigenerando i codici, quelli precedenti NON funzioneranno più. Continuare?")) return;
    setBkBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/backup-codes/generate");
      setBkCodes(data.codes || []);
      setBkOpen(true);
      await loadBk();
    } catch (e) {
      toast.error(extractError(e, "Impossibile generare i codici di riserva."));
    } finally { setBkBusy(false); }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(bkCodes.join("\n"));
      toast.success("Codici copiati negli appunti.");
    } catch { toast.error("Copia non riuscita. Copia i codici manualmente."); }
  };

  const downloadCodes = () => {
    const header = "INSIEME RAS - Codici di riserva (accesso)\nUtente: " + (user?.email || "") +
      "\nGenerati il: " + new Date().toLocaleString("it-IT") +
      "\n\nOgni codice è utilizzabile UNA SOLA VOLTA. Conservali in luogo sicuro.\n\n";
    const blob = new Blob([header + bkCodes.join("\n") + "\n"], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "codici-riserva-insieme-ras.txt";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Gestione ritorno dal flusso OAuth (?calendar=connected|error)
  useEffect(() => {
    const c = params.get("calendar");
    if (c === "connected") { toast.success("Google Calendar collegato correttamente"); loadCal(); params.delete("calendar"); setParams(params, { replace: true }); }
    else if (c === "error") { toast.error("Collegamento a Google Calendar non riuscito. Riprova."); params.delete("calendar"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/login");
      if (data?.authorization_url) window.location.href = data.authorization_url;
      else toast.error("Impossibile avviare il collegamento");
    } catch (e) {
      toast.error(extractError(e, "Integrazione Google Calendar non configurata."));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar? I nuovi appuntamenti non saranno più sincronizzati.")) return;
    setBusy(true);
    try { await api.post("/oauth/calendar/disconnect"); toast.success("Google Calendar scollegato"); loadCal(); }
    catch (e) { toast.error(extractError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader icon={UserCircle} title="Profilo personale" description="I tuoi dati, permessi e integrazioni" />
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-[color:var(--brand-blue-600)] text-white flex items-center justify-center text-xl font-semibold">{(user?.nome_completo || "?").split(" ").slice(0,2).map(s=>s[0]).join("")}</div>
            <div><div className="font-display text-xl font-semibold">{user?.nome_completo}</div><div className="text-sm text-muted-foreground">{user?.email}</div></div>
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between border-b py-1"><span className="text-muted-foreground">Ruolo</span><span className="font-medium">{user?.ruolo_label || user?.ruolo}</span></div>
            <div className="flex justify-between border-b py-1"><span className="text-muted-foreground">Stato</span><span className="font-medium">{user?.attivo ? "Attivo" : "Disattivato"}</span></div>
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold mb-2">Permessi</h3>
          <div className="flex flex-wrap gap-1.5">
            {(user?.permessi || []).map((p) => <span key={p} className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5 font-mono">{p}</span>)}
          </div>
        </Card>
      </div>

      <Card className="p-5 mt-4" data-testid="calendar-integration-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[color:var(--brand-blue-100)] text-[color:var(--brand-blue-700)] flex items-center justify-center">
              <Calendar className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold">Google Calendar</h3>
              <p className="text-sm text-muted-foreground">Sincronizza i tuoi appuntamenti dell'Agenda con il tuo calendario Google.</p>
            </div>
          </div>
          {cal?.connected ? (
            <span className="inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)]" data-testid="calendar-connected-badge">
              <CheckCircle2 className="h-3.5 w-3.5" /> Collegato
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 bg-slate-100 text-slate-500" data-testid="calendar-disconnected-badge">
              Non collegato
            </span>
          )}
        </div>

        <div className="mt-4">
          {!cal?.configured && (
            <div className="flex items-start gap-2 rounded-lg border bg-[color:var(--brand-orange-100)]/40 p-3 text-sm text-slate-600 mb-3">
              <AlertCircle className="h-4 w-4 text-[color:var(--brand-orange-600)] shrink-0 mt-0.5" />
              <span>Integrazione non ancora configurata dall'amministratore. Sarà disponibile appena inserite le credenziali Google Cloud (istruzioni in <code className="text-xs bg-muted px-1 py-0.5 rounded">/app/memory/google_calendar_setup.md</code>).</span>
            </div>
          )}
          {cal?.connected ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">Account collegato: <span className="font-medium text-slate-700">{cal.email || "-"}</span></p>
              <Button variant="outline" onClick={disconnect} disabled={busy} className="text-[color:var(--brand-red-600)]" data-testid="calendar-disconnect-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Unlink className="h-4 w-4 mr-2" />} Scollega
              </Button>
            </div>
          ) : (
            <Button onClick={connect} disabled={busy || !cal?.configured} className="bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid="calendar-connect-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Link2 className="h-4 w-4 mr-2" />} Collega Google Calendar
            </Button>
          )}
        </div>
      </Card>

      <Card className="p-5 mt-4" data-testid="backup-codes-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[color:var(--brand-orange-100)] text-[color:var(--brand-orange-600)] flex items-center justify-center">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold">Verifica in due passaggi · Codici di riserva</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Codici di emergenza monouso da usare per accedere se non ricevi l'email con il codice di verifica.
              </p>
            </div>
          </div>
          {bk?.remaining > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 bg-[color:var(--brand-blue-100)] text-[color:var(--brand-blue-700)]" data-testid="backup-codes-remaining-badge">
              <KeyRound className="h-3.5 w-3.5" /> {bk.remaining} codici disponibili
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 bg-slate-100 text-slate-500" data-testid="backup-codes-none-badge">
              Nessun codice attivo
            </span>
          )}
        </div>

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Button
            onClick={generateBackupCodes}
            disabled={bkBusy}
            className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white"
            data-testid="backup-codes-generate-button">
            {bkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : (bk?.remaining > 0 ? <RefreshCw className="h-4 w-4 mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />)}
            {bk?.remaining > 0 ? "Rigenera codici" : "Genera codici di riserva"}
          </Button>
          {bk?.remaining > 0 && (
            <p className="text-xs text-muted-foreground">
              Conserva i codici in un luogo sicuro: ciascuno è utilizzabile una sola volta.
            </p>
          )}
        </div>
      </Card>

      {/* Dialog: mostra i codici generati una sola volta */}
      <Dialog open={bkOpen} onOpenChange={setBkOpen}>
        <DialogContent data-testid="backup-codes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[color:var(--brand-orange-600)]" /> I tuoi codici di riserva
            </DialogTitle>
            <DialogDescription>
              Salvali ora: per sicurezza non potrai più rivederli. Ogni codice funziona una sola volta.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-start gap-2 rounded-lg border bg-[color:var(--brand-orange-100)]/50 p-3 text-xs text-slate-600">
            <AlertCircle className="h-4 w-4 text-[color:var(--brand-orange-600)] shrink-0 mt-0.5" />
            <span>Gli eventuali codici generati in precedenza non sono più validi.</span>
          </div>

          <div className="grid grid-cols-2 gap-2 my-2" data-testid="backup-codes-list">
            {bkCodes.map((c, i) => (
              <div key={c} className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-sm tracking-widest text-center" data-testid={`backup-code-${i}`}>
                {c}
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={copyCodes} data-testid="backup-codes-copy-button">
              <Copy className="h-4 w-4 mr-2" /> Copia
            </Button>
            <Button variant="outline" onClick={downloadCodes} data-testid="backup-codes-download-button">
              <Download className="h-4 w-4 mr-2" /> Scarica .txt
            </Button>
            <Button onClick={() => setBkOpen(false)} className="bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid="backup-codes-done-button">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Li ho salvati
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
